import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { FieldInputProps, FieldMetaProps, useFormikContext } from "formik";
import { Box, ClassNameMap, Grid, TextField, Typography } from "@mui/material";

import { CalculEditorSimulationFormDetails } from "../interfaces";
import { TooltipLabel } from "../../components/Tooltips";
import { transformValue } from "./CalculEditorInput";
import { GenericObject } from "../../services/Price";
import { useGetWatchedFieldValue } from "../utils";
import { getCalculationSimulationResult } from "../../Alert/Filter/utils";
import { useDebounce } from "../../hooks/debounce";

type parsedValueType = { type: "text" | "variable"; value: string };
type extendedParsedValueType = parsedValueType & { newValue?: string };

/**
 * Get string and return array of object with identifier on each part of input value
 * to identify wich part is text and wich part is variable
 *
 * @param filter To get all variables in input value
 * @param calculatorValue input value
 */
const inputValueParser = (
  filter: CalculEditorSimulationFormDetails,
  calculatorValue: string,
  variablesValue?: GenericObject
) => {
  let currValue: string = calculatorValue ?? "";
  const parsedValue: extendedParsedValueType[] = [];

  const variables = filter.editorInputs
    ? filter.editorInputs
        .filter((input) => input.isVariable)
        .map((input) => input.value)
    : [];

  while (currValue.length > 0) {
    let firstMatchPos = Infinity;
    let currVariable = "";

    // Find first variable present in currValue
    for (const variable of variables) {
      const idx = currValue.indexOf(variable);
      if (idx !== -1 && idx < firstMatchPos) {
        firstMatchPos = idx;
        currVariable = variable;
      }
    }

    // If no variable are find we skip the loop
    if (firstMatchPos === Infinity) {
      parsedValue.push({ type: "text", value: currValue });
      break;
    }

    // Add text finded before first variable if it's the case in the parser
    if (firstMatchPos > 0) {
      parsedValue.push({
        type: "text",
        value: currValue.substring(0, firstMatchPos),
      });
    }

    // Add variable to parser
    parsedValue.push({
      type: "variable",
      value: currValue.substring(
        firstMatchPos,
        firstMatchPos + currVariable.length
      ),
      newValue: variablesValue ? variablesValue[currVariable] ?? null : null,
    });

    // Cut currValue for the next loop
    currValue = currValue.substring(firstMatchPos + currVariable.length);
  }

  return parsedValue;
};

/**
 * Transform parsed value into string
 */
const valueFormatter = (parsedValue: extendedParsedValueType[]): string => {
  const newValue = parsedValue.map((el) =>
    el.newValue ? el.newValue : el.value
  );
  return newValue.join("");
};

const AutoFilledInput = memo(
  ({
    label,
    input,
    values,
    classes,
    onChange,
  }: {
    label: string;
    input: any;
    values: GenericObject;
    classes: any;
    onChange: (fieldName: string, value: string) => void;
  }) => {
    const intl = useIntl();
    const watchedFieldValue: GenericObject | undefined =
      useGetWatchedFieldValue(values, input.key, input.watchField);

    useEffect(() => {
      if (watchedFieldValue && watchedFieldValue[input.key]) {
        onChange(input.value, watchedFieldValue[input.key]);
      } else {
        onChange(input.value, input.defaultValue);
      }
    }, [
      watchedFieldValue,
      input.key,
      input.defaultValue,
      onChange,
      input.value,
    ]);

    return (
      <TextField
        className={classes.inputText}
        variant="outlined"
        disabled
        fullWidth
        label={
          input.tooltip ? (
            <TooltipLabel
              label={label}
              tooltipLabel={intl.formatMessage(
                { id: input.tooltip },
                { br: <br /> }
              )}
            />
          ) : (
            label
          )
        }
        placeholder={input.placeholder || undefined}
        value={
          watchedFieldValue && watchedFieldValue[input.key]
            ? watchedFieldValue[input.key]
            : input.defaultValue
        }
      />
    );
  }
);

const InputsVariables = memo(
  ({
    formValues,
    filter,
    inputValueParsed,
    classes,
    onChange,
  }: {
    formValues: GenericObject;
    filter: CalculEditorSimulationFormDetails;
    inputValueParsed: parsedValueType[];
    classes: ClassNameMap;
    onChange: (fieldName: string, value: string) => void;
  }) => {
    const intl = useIntl();
    const inputs = useMemo(
      () => filter.editorInputs || [],
      [filter.editorInputs]
    );
    const variables = useMemo(
      () =>
        inputValueParsed
          .filter((el) => el.type === "variable")
          .map((el) => el.value),
      [inputValueParsed]
    );

    if (inputs.length === 0) return null;

    return (
      <Grid container spacing={1} sx={{ marginTop: 2 }}>
        {inputs.map((input) => {
          const label = intl.formatMessage({ id: input.label });
          if (input?.type === "autofill") {
            return (
              <Grid size={6} key={input.value}>
                <AutoFilledInput
                  label={label}
                  input={input}
                  values={formValues}
                  classes={classes}
                  onChange={onChange}
                />
              </Grid>
            );
          }
          return (
            <Grid size={6} key={input.value}>
              <TextField
                type="number"
                defaultValue={input.defaultValue}
                label={label}
                disabled={!variables.includes(input.value)}
                className={classes.inputText}
                placeholder={input.placeholder || undefined}
                onChange={(e) => onChange(input.value, e.target.value)}
              />
            </Grid>
          );
        })}
      </Grid>
    );
  }
);

const CalculEditorSimulationInput = ({
  classes,
  field,
  filter,
  meta,
}: {
  classes: ClassNameMap;
  field: FieldInputProps<any>;
  filter: CalculEditorSimulationFormDetails;
  meta: FieldMetaProps<any>;
}) => {
  const intl = useIntl();
  const { values } = useFormikContext<any>();
  const calculatorValue = useMemo(
    () => values[filter.watchField as any] ?? "",
    [values, filter.watchField]
  );
  const [inputValueParsed, setInputValueParsed] = useState(
    inputValueParser(filter, calculatorValue)
  );
  const [inputValue, setInputValue] = useState(
    transformValue(calculatorValue, filter, intl, false)
  );
  const [variablesValue, setVariablesValue] = useState<Record<string, string>>(
    {}
  );
  const variables = useMemo(
    () =>
      inputValueParsed
        .filter((part: parsedValueType) => part.type === "variable")
        .map((part: parsedValueType) => part.value),
    [inputValueParsed]
  );
  const roundingValue = useMemo(
    () => ({
      rounding_value_v2: values.rounding_value_v2,
      rounding_direction_v2: values.rounding_direction_v2,
    }),
    [values]
  );

  // Debounce to add rate limiter
  const debouncedCalculatorValue = useDebounce(calculatorValue, 1000);
  const debouncedVariablesValue = useDebounce(variablesValue, 1000);
  const debouncedRoundingValue = useDebounce(roundingValue, 1000);

  const query = useQuery(
    [
      "calculator_simulation",
      debouncedCalculatorValue,
      debouncedVariablesValue,
      debouncedRoundingValue,
      variables,
    ],
    () =>
      getCalculationSimulationResult(
        filter.api || "",
        debouncedCalculatorValue,
        debouncedVariablesValue,
        debouncedRoundingValue
      ),
    {
      enabled:
        !!filter.api &&
        !!debouncedCalculatorValue &&
        variables.every(
          (variable) =>
            debouncedVariablesValue.hasOwnProperty(variable) &&
            debouncedVariablesValue[variable] !== ""
        ),
    }
  );

  useEffect(() => {
    setInputValueParsed(
      inputValueParser(filter, calculatorValue, variablesValue)
    );
  }, [calculatorValue, filter, intl, variablesValue]);

  useEffect(() => {
    setInputValue(
      transformValue(valueFormatter(inputValueParsed), filter, intl, false)
    );
  }, [inputValueParsed, filter, intl]);

  const handleChange = useCallback((fieldName: string, value: string) => {
    setVariablesValue((prev) => ({ ...prev, [fieldName]: value }));
  }, []);

  return (
    <Box>
      <TooltipLabel
        sxStyles={{
          color: "text.secondary",
          fontWeight: 700,
          "& svg": { fontSize: "0.938rem" },
        }}
        label={intl.formatMessage({ id: filter.label })}
        tooltipLabel={intl.formatMessage(
          { id: filter.tooltip },
          { br: <br /> }
        )}
        translateTooltip={false}
      />
      <InputsVariables
        formValues={values}
        filter={filter}
        inputValueParsed={inputValueParsed}
        classes={classes}
        onChange={handleChange}
      />
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        <TextField
          {...field}
          disabled
          className={classes.inputText}
          variant="outlined"
          fullWidth
          value={inputValue}
          sx={{ marginBottom: 0 }}
        />
        =
        <TextField
          {...field}
          disabled
          className={classes.inputText}
          variant="outlined"
          fullWidth
          value={query.data ?? ""}
          sx={{ marginBottom: 0, width: "100px !important" }}
        />
      </Box>
      {meta.touched && meta.error && (
        <Typography
          sx={{
            color: "error.main",
            fontSize: "0.75rem",
            margin: "4px 14px 0",
          }}
        >
          {meta.error}
        </Typography>
      )}
    </Box>
  );
};

export default CalculEditorSimulationInput;
